import React, { useState, useEffect, useRef, useCallback } from 'react';
import ConfigPanel from './components/ConfigPanel.tsx';
import ChatMessage from './components/ChatMessage.tsx';
import ChatInput from './components/ChatInput.tsx';
import Sidebar from './components/Sidebar.tsx';
import DataManager from './components/DataManager.tsx';
import { Message, ChatConfig, ChatSession, AppState } from './types';
import { ChatService, LegacyChatConfig } from './services/chatService.ts';
import { StorageService } from './services/storageService.ts';
import './App.css';

// 清理存储服务
StorageService.cleanup();

function App() {
  const [config, setConfig] = useState<ChatConfig>(() => {
    return StorageService.getChatConfig();
  });

  const [appState, setAppState] = useState<AppState>(() => {
    return StorageService.getAppState();
  });

  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [isDataManagerOpen, setIsDataManagerOpen] = useState(false);
  const [isSidebarHidden, setIsSidebarHidden] = useState(() => {
    return StorageService.getSidebarHidden();
  });

  // 创建ChatService实例
  const createChatService = useCallback((): ChatService => {
    const legacyConfig: LegacyChatConfig = {
      apiUrl: config.baseUrl,
      apiKey: config.apiKey,
      model: config.currentModel
    };
    return new ChatService(legacyConfig);
  }, [config.baseUrl, config.apiKey, config.currentModel]);

  const [chatService, setChatService] = useState(() => createChatService());

  const messagesEndRef = useRef<HTMLDivElement>(null);

  // 当配置改变时更新ChatService
  useEffect(() => {
    setChatService(createChatService());
  }, [createChatService]);

  // 保存配置到本地存储
  useEffect(() => {
    StorageService.saveChatConfig(config);
  }, [config]);

  // 保存应用状态到本地存储
  useEffect(() => {
    StorageService.saveAppState(appState);
  }, [appState]);

  // 保存sidebar状态到本地存储
  useEffect(() => {
    StorageService.saveSidebarHidden(isSidebarHidden);
  }, [isSidebarHidden]);

  // 自动滚动到底部
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [appState.sessions, appState.isLoading]);

  const getCurrentSession = (): ChatSession | null => {
    if (!appState.currentSessionId) return null;
    return appState.sessions.find(s => s.id === appState.currentSessionId) || null;
  };

  const createNewSession = () => {
    const newSession: ChatSession = {
      id: Date.now().toString(),
      title: '新对话',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      model: config.currentModel
    };

    setAppState(prev => ({
      ...prev,
      sessions: [newSession, ...prev.sessions],
      currentSessionId: newSession.id,
      error: null
    }));
  };

  const selectSession = (sessionId: string) => {
    setAppState(prev => ({
      ...prev,
      currentSessionId: sessionId,
      error: null
    }));
  };

  const deleteSession = (sessionId: string) => {
    setAppState(prev => {
      const newSessions = prev.sessions.filter(s => s.id !== sessionId);
      let newCurrentSessionId = prev.currentSessionId;
      
      // 如果删除的是当前会话，选择第一个会话或设为null
      if (sessionId === prev.currentSessionId) {
        newCurrentSessionId = newSessions.length > 0 ? newSessions[0].id : null;
      }
      
      return {
        ...prev,
        sessions: newSessions,
        currentSessionId: newCurrentSessionId,
        error: null
      };
    });
  };

  const renameSession = (sessionId: string, newTitle: string) => {
    setAppState(prev => ({
      ...prev,
      sessions: prev.sessions.map(s => 
        s.id === sessionId 
          ? { ...s, title: newTitle, updatedAt: new Date() }
          : s
      )
    }));
  };

  const updateSessionMessages = (sessionId: string, messages: Message[]) => {
    setAppState(prev => ({
      ...prev,
      sessions: prev.sessions.map(s => 
        s.id === sessionId 
          ? { ...s, messages, updatedAt: new Date() }
          : s
      )
    }));
  };

  const handleSendMessage = async (content: string) => {
    if (!content.trim()) return;

    const currentSession = getCurrentSession();
    if (!currentSession) {
      // 创建新会话并直接处理消息，避免递归调用
      const newSession: ChatSession = {
        id: Date.now().toString(),
        title: '新对话',
        messages: [],
        createdAt: new Date(),
        updatedAt: new Date(),
        model: config.currentModel
      };

      const userMessage: Message = {
        id: Date.now().toString(),
        content,
        role: 'user',
        timestamp: new Date()
      };

      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: '',
        role: 'assistant',
        timestamp: new Date(),
        isStreaming: true,
        isComplete: false
      };

      const newMessages = [userMessage, assistantMessage];
      const sessionWithMessages = { ...newSession, messages: newMessages };

      setAppState(prev => ({
        ...prev,
        sessions: [sessionWithMessages, ...prev.sessions],
        currentSessionId: newSession.id,
        error: null,
        isLoading: true
      }));

      // 验证配置
      const validation = chatService.validateConfig();
      if (!validation.isValid) {
        setAppState(prev => ({
          ...prev,
          isLoading: false,
          error: validation.errors.join(', ')
        }));
        setIsConfigOpen(true);
        return;
      }

      try {
        await chatService.sendMessageStream([userMessage], (chunk) => {
          if (chunk.error) {
            setAppState(prev => ({
              ...prev,
              isLoading: false,
              error: chunk.error || null
            }));
            return;
          }

          if (chunk.done) {
            // 流式输出完成
            setAppState(prev => {
              const updatedSessions = prev.sessions.map(s => 
                s.id === newSession.id 
                  ? {
                      ...s,
                      messages: s.messages.map(msg => 
                        msg.id === assistantMessage.id 
                          ? { ...msg, isStreaming: false, isComplete: true }
                          : msg
                      ),
                      updatedAt: new Date()
                    }
                  : s
              );
              return {
                ...prev,
                sessions: updatedSessions,
                isLoading: false
              };
            });
          } else {
            // 更新流式内容
            setAppState(prev => {
              const updatedSessions = prev.sessions.map(s => 
                s.id === newSession.id 
                  ? {
                      ...s,
                      messages: s.messages.map(msg => 
                        msg.id === assistantMessage.id 
                          ? { ...msg, content: msg.content + chunk.content }
                          : msg
                      ),
                      updatedAt: new Date()
                    }
                  : s
              );
              return {
                ...prev,
                sessions: updatedSessions
              };
            });
          }
        });
      } catch (error) {
        setAppState(prev => ({
          ...prev,
          isLoading: false,
          error: error instanceof Error ? error.message : '发送消息时发生错误'
        }));
      }
      return;
    }

    // 验证配置
    const validation = chatService.validateConfig();
    if (!validation.isValid) {
      setAppState(prev => ({
        ...prev,
        error: validation.errors.join(', ')
      }));
      setIsConfigOpen(true);
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      content,
      role: 'user',
      timestamp: new Date()
    };

    const assistantMessage: Message = {
      id: (Date.now() + 1).toString(),
      content: '',
      role: 'assistant',
      timestamp: new Date(),
      isStreaming: true,
      isComplete: false
    };

    const newMessages = [...currentSession.messages, userMessage, assistantMessage];
    updateSessionMessages(currentSession.id, newMessages);
    
    setAppState(prev => ({
      ...prev,
      isLoading: true,
      error: null
    }));

    try {
      const allMessages = [...currentSession.messages, userMessage];
      
      await chatService.sendMessageStream(allMessages, (chunk) => {
        if (chunk.error) {
          setAppState(prev => ({
            ...prev,
            isLoading: false,
            error: chunk.error || null
          }));
          return;
        }

        if (chunk.done) {
          // 流式输出完成
          setAppState(prev => {
            const updatedSessions = prev.sessions.map(s => 
              s.id === currentSession.id 
                ? {
                    ...s,
                    messages: s.messages.map(msg => 
                      msg.id === assistantMessage.id 
                        ? { ...msg, isStreaming: false, isComplete: true }
                        : msg
                    ),
                    updatedAt: new Date()
                  }
                : s
            );
            return {
              ...prev,
              sessions: updatedSessions,
              isLoading: false
            };
          });
        } else {
          // 更新流式内容
          setAppState(prev => {
            const updatedSessions = prev.sessions.map(s => 
              s.id === currentSession.id 
                ? {
                    ...s,
                    messages: s.messages.map(msg => 
                      msg.id === assistantMessage.id 
                        ? { ...msg, content: msg.content + chunk.content }
                        : msg
                    ),
                    updatedAt: new Date()
                  }
                : s
            );
            return {
              ...prev,
              sessions: updatedSessions
            };
          });
        }
      });
    } catch (error) {
      setAppState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : '发送消息时发生错误'
      }));
    }
  };

  const handleConfigChange = (newConfig: ChatConfig) => {
    setConfig(newConfig);
  };

  const clearCurrentChat = () => {
    const currentSession = getCurrentSession();
    if (currentSession) {
      updateSessionMessages(currentSession.id, []);
    }
  };

  const currentSession = getCurrentSession();
  const currentUrl = config.baseUrl; // Assuming baseUrl is the current URL for display

  // 获取模型的显示名称
  const getModelDisplayName = (modelValue: string): string => {
    if (!config.models || !Array.isArray(config.models)) {
      return modelValue;
    }
    const model = config.models.find(m => m.value === modelValue);
    return model?.displayName || modelValue;
  };

  // 检查是否有有效的配置
  const hasValidConfig = (): boolean => {
    return !!(config.apiKey && config.baseUrl && config.currentModel && config.models && config.models.length > 0);
  };

  return (
    <div className="app">
      {!isSidebarHidden && (
        <Sidebar
          sessions={appState.sessions}
          currentSessionId={appState.currentSessionId}
          onSessionSelect={selectSession}
          onNewSession={createNewSession}
          onDeleteSession={deleteSession}
          onRenameSession={renameSession}
          onHideSidebar={() => setIsSidebarHidden(true)}
        />
      )}

      <div className="main-content">
        <header className="app-header">
          <div className="header-content">
            <div className="header-left">
              {isSidebarHidden && (
                <button 
                  className="btn btn-secondary sidebar-toggle"
                  onClick={() => {
                    setIsSidebarHidden(false);
                  }}
                  title="显示侧边栏"
                >
                  ☰
                </button>
              )}
              <h1>
                🤖 {currentUrl || '未配置'} - {getModelDisplayName(config.currentModel)}
              </h1>
            </div>
            <div className="header-actions">
              {currentSession && (
                <button 
                  className="btn btn-secondary"
                  onClick={clearCurrentChat}
                  disabled={currentSession.messages.length === 0}
                  title="清空对话"
                >
                  🗑️
                </button>
              )}
              <button 
                className="btn btn-secondary"
                onClick={() => setIsDataManagerOpen(true)}
                title="数据管理"
              >
                💾
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => setIsConfigOpen(true)}
                title="配置"
              >
                ⚙️
              </button>
            </div>
          </div>
        </header>

        <main className="chat-container">
          <div className="messages-container">
            {!currentSession ? (
              <div className="empty-state">
                <div className="empty-icon">💬</div>
                <h3>开始你的AI对话</h3>
                <p>在左侧创建新的聊天窗口，开始与AI助手对话</p>
              
              </div>
            ) : currentSession.messages.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">💬</div>
                <h3>开始你的AI对话</h3>
                <p>在下方输入框中输入你的问题，AI助手将为你提供帮助</p>
              </div>
            ) : (
              currentSession.messages.map((message) => (
                <ChatMessage key={message.id} message={message} />
              ))
            )}

            {appState.error && (
              <div className="error-message">
                <div className="error-icon">⚠️</div>
                <div className="error-text">{appState.error}</div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <ChatInput
            onSendMessage={handleSendMessage}
            isLoading={appState.isLoading}
            disabled={!hasValidConfig()}
          />
        </main>
      </div>

      <ConfigPanel
        config={config}
        onConfigChange={handleConfigChange}
        isOpen={isConfigOpen}
        onToggle={() => setIsConfigOpen(false)}
      />

      <DataManager
        isOpen={isDataManagerOpen}
        onClose={() => setIsDataManagerOpen(false)}
      />
    </div>
  );
}

export default App; 